import type { ModuleData, ModuleId } from '../content'
import type { GroupId } from '../config/systemConfig'
import { getFamilyDefinition } from '../content'
import { hasActiveDecay } from './masteryDecay'
import { getWeakestFamilyEntries } from './masteryCanonical'
import type { FamilyMasteryState } from './familyMasteryEngine'
import {
  MASTERY_THRESHOLDS,
  MASTERY_LEVEL_THRESHOLDS,
} from '../config/constants'

/**
 * Smart Sessions (F4.1) — consolidates "Ga verder", "Djay raadt aan" and
 * "Herhaal-tip" into three explicit segments.
 *
 * Pure function. UI lives in SmartSessionCard.
 */

export type SmartSessionKind = 'review' | 'fresh' | 'almostExpert'

export interface SmartSessionSegment {
  kind: SmartSessionKind
  module: ModuleData
  /** Short noun phrase used as the segment subtitle. */
  subtitle: string
  /** Optional family that motivated the suggestion (for review). */
  familyId?: string
}

export interface SmartSessions {
  review?: SmartSessionSegment
  fresh?: SmartSessionSegment
  almostExpert?: SmartSessionSegment
  /** Which segment Djay highlights. Undefined when no segments are available. */
  primary?: SmartSessionKind
}

export function getSmartSessions(
  familyMastery: Record<string, FamilyMasteryState>,
  modules: ModuleData[],
  selectedGroup: GroupId,
  getModuleMasteryForGroup: (moduleId: ModuleId) => number,
  getAnsweredQuestions: (moduleId: ModuleId) => unknown[],
): SmartSessions {
  if (!modules || modules.length === 0) return {}

  const inProgress = modules.filter((m) => {
    const mastery = getModuleMasteryForGroup(m.id)
    const answered = getAnsweredQuestions(m.id).length
    return (
      (answered > 0 || mastery > 0) && mastery < MASTERY_THRESHOLDS.MASTERED
    )
  })
  const untouched = modules.filter((m) => {
    const mastery = getModuleMasteryForGroup(m.id)
    const answered = getAnsweredQuestions(m.id).length
    return answered === 0 && mastery === 0
  })

  // ── Review: weakest family with active decay → its module ─────────────
  // Uses the *entries* variant so we never lose the group-scoped key. The
  // older `getWeakestFamiliesGlobal` returned `FamilyMasteryState[]` (no key
  // field), which caused F-MAS-R1-SMARTSESSIONS-DECAY-DEAD-PATH — consumers
  // tried `w.key` and silently got `undefined`. Resolved by switching to
  // `getWeakestFamilyEntries` (F-MAS-R2-WEAKEST-GLOBAL-API-LOSES-KEY).
  let review: SmartSessionSegment | undefined
  const weakest = getWeakestFamilyEntries(familyMastery, selectedGroup, 12)
  const decaying = weakest.filter((entry) => hasActiveDecay(entry.state))
  if (decaying.length > 0) {
    const top = decaying[0].state
    const def = getFamilyDefinition(top.familyId)
    const moduleId = def?.module
    const module = moduleId ? modules.find((m) => m.id === moduleId) : undefined
    if (module && def) {
      const others = decaying.length - 1
      review = {
        kind: 'review',
        module,
        subtitle:
          others > 0
            ? `${def.description ?? top.familyId} +${others} meer`
            : (def.description ?? top.familyId),
        familyId: top.familyId,
      }
    }
  }
  // Fallback: lowest in-progress module under BEGINNER threshold
  if (!review && inProgress.length > 0) {
    let weakestModule: ModuleData | null = null
    let lowest = MASTERY_LEVEL_THRESHOLDS.BEGINNER
    for (const m of inProgress) {
      const mastery = getModuleMasteryForGroup(m.id)
      if (mastery < lowest) {
        lowest = mastery
        weakestModule = m
      }
    }
    if (weakestModule) {
      review = {
        kind: 'review',
        module: weakestModule,
        subtitle: 'Hier valt nog winst',
      }
    }
  }

  // ── Fresh: first untouched module ─────────────────────────────────────
  let fresh: SmartSessionSegment | undefined
  if (untouched.length > 0) {
    fresh = {
      kind: 'fresh',
      module: untouched[0],
      subtitle: 'Nog niet geprobeerd',
    }
  }

  // ── Almost expert: in-progress module with highest mastery < 95 ───────
  let almostExpert: SmartSessionSegment | undefined
  if (inProgress.length > 0) {
    let best: ModuleData | null = null
    let bestScore = MASTERY_LEVEL_THRESHOLDS.MASTERED // require ≥ 85
    for (const m of inProgress) {
      const mastery = getModuleMasteryForGroup(m.id)
      if (mastery >= bestScore && mastery < MASTERY_THRESHOLDS.MASTERED) {
        bestScore = mastery
        best = m
      }
    }
    // Fallback: any in-progress module ≥ LEARNING (70) and not the review pick
    if (!best) {
      let bestScoreSoft = MASTERY_LEVEL_THRESHOLDS.LEARNING
      for (const m of inProgress) {
        const mastery = getModuleMasteryForGroup(m.id)
        if (
          mastery >= bestScoreSoft &&
          mastery < MASTERY_THRESHOLDS.MASTERED &&
          (!review || m.id !== review.module.id)
        ) {
          bestScoreSoft = mastery
          best = m
        }
      }
    }
    if (best) {
      const score = Math.round(getModuleMasteryForGroup(best.id))
      almostExpert = {
        kind: 'almostExpert',
        module: best,
        subtitle: `${score}% — bijna af`,
      }
    }
  }

  // ── Primary pick: Djay's recommendation ──────────────────────────────
  // Priority: review (decay) > almostExpert (motivation: finish strong) > fresh
  let primary: SmartSessionKind | undefined
  if (review && review.familyId) primary = 'review'
  else if (almostExpert) primary = 'almostExpert'
  else if (fresh) primary = 'fresh'
  else if (review) primary = 'review'

  return { review, fresh, almostExpert, primary }
}
